import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignIn, useSignUp } from '@clerk/clerk-react';
import { Alert, Form, Input, Typography } from 'antd';

import { Button } from '@/components/Button';
import { ROUTES } from '@/routes';
import { getAndClearRedirectPath, parseClerkError } from '@/utils';
import styles from './VerifyDevice.module.scss';

interface VerifyDeviceFormValues {
  code: string;
}

export const VerifyDevice: React.FC = () => {
  const { signIn, isLoaded: signInLoaded, setActive: setSignInActive } = useSignIn();
  const { signUp, isLoaded: signUpLoaded, setActive: setSignUpActive } = useSignUp();
  const isLoaded = signInLoaded && signUpLoaded;
  const navigate = useNavigate();
  const [form] = Form.useForm<VerifyDeviceFormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: VerifyDeviceFormValues) => {
    if (!isLoaded) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (signUp?.status === 'missing_requirements') {
        const result = await signUp.attemptEmailAddressVerification({ code: values.code });
        if (result.status === 'complete') {
          await setSignUpActive({ session: result.createdSessionId });
          navigate(ROUTES.SESSIONS, { replace: true });
        } else {
          setError('Verification incomplete. Please try registering again.');
          navigate(ROUTES.REGISTER);
        }
      } else {
        if (!signIn) return;
        const result = await signIn.attemptFirstFactor({ strategy: 'email_code', code: values.code });
        if (result.status === 'complete') {
          await setSignInActive({ session: result.createdSessionId });
          const redirectUrl = getAndClearRedirectPath() ?? ROUTES.SESSIONS;
          navigate(redirectUrl);
        } else {
          setError('Verification incomplete. Please sign in again.');
          navigate(ROUTES.LOGIN);
        }
      }
    } catch (err) {
      setError(parseClerkError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Typography.Title level={1} className={styles.title}>
        Verify your device
      </Typography.Title>
      <Typography.Text className={styles.subtitle}>
        We sent a code to your email address. Enter it below to complete sign in.
      </Typography.Text>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className={styles.alert}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        colon={false}
        className={styles.form}
      >
        <Form.Item
          name="code"
          label="Verification code"
          rules={[{ required: true, message: 'Please enter the verification code' }]}
        >
          <Input.OTP length={6} size="large" disabled={isSubmitting} />
        </Form.Item>

        <Button appearance="solid" htmlType="submit" size="large" block loading={isSubmitting} disabled={!isLoaded}>
          Verify
        </Button>
      </Form>
    </div>
  );
};

export default VerifyDevice;
